import React, { useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { useSelector } from 'react-redux';
import { api } from 'common';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { C2Select, C2SummaryCard } from '../components/Table/C2UserTable';
import C2ReportTable, { C2Cell, formatCop, monthOptions } from '../components/reports/C2ReportTable';

export default function FleetAdminEarning({ bookings, loading }) {
  const users = useSelector((state) => state.usersdata.users);
  const usersById = useMemo(() => {
    const m = {};
    (users || []).forEach((u) => {
      m[u.id] = u;
    });
    return m;
  }, [users]);

  const rows = useMemo(() => api.buildFleetEarnings(bookings || [], usersById), [bookings, usersById]);
  const months = useMemo(() => monthOptions(rows, { allLabel: 'Todos los meses' }), [rows]);
  const [pickedMonth, setMonth] = useState(null);
  const month = pickedMonth ?? (months[1] ? months[1].value : '');

  const filtered = useMemo(
    () => rows.filter((r) => !month || r.monthKey === month).map((r) => ({ ...r, monthLabel: api.reportMonthLabel(r.monthKey) })),
    [rows, month]
  );

  const summary = useMemo(
    () => ({
      fleets: new Set(filtered.map((r) => r.fleetUid)).size,
      rides: filtered.reduce((acc, r) => acc + r.rides, 0),
      commission: filtered.reduce((acc, r) => acc + r.commission, 0),
    }),
    [filtered]
  );

  const columns = [
    { title: 'Mes', field: 'monthLabel', customSort: (a, b) => a.monthKey.localeCompare(b.monthKey) },
    { title: 'Flota', field: 'fleetName', render: (r) => <C2Cell primary={r.fleetName} /> },
    { title: 'ID de flota', field: 'fleetUid', hidden: true, export: true },
    { title: 'Viajes', field: 'rides', type: 'numeric' },
    { title: 'Comisión de la flota', field: 'commission', type: 'numeric', render: (r) => <C2Cell primary={formatCop(r.commission)} color="#15803D" /> },
  ];

  const periodCaption = month ? api.reportMonthLabel(month) : 'Todos los meses';

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <C2SummaryCard icon={BusinessOutlinedIcon} label="Flotas con comisión" value={summary.fleets} caption={periodCaption} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <C2SummaryCard icon={RouteOutlinedIcon} label="Viajes de flotas" value={summary.rides} caption={periodCaption} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <C2SummaryCard icon={PaymentsOutlinedIcon} label="Comisión de flotas" value={formatCop(summary.commission)} caption={periodCaption} accent="#16A34A" />
        </Grid>
      </Grid>
      <C2ReportTable
        title="Ganancias de flotas"
        subtitle="Comisión que recibió cada administrador de flota por los viajes de sus conductores."
        columns={columns}
        data={filtered}
        loading={loading}
        emptyIcon={BusinessOutlinedIcon}
        emptyTitle="Sin comisiones de flota en este periodo"
        emptyMessage="Cuando los conductores de una flota completen viajes, la comisión aparecerá aquí."
        csvFileName="ganancias_flotas.csv"
        toolbarExtras={[
          <C2Select key="m" id="fleet-earning-month" label="Mes" value={month} onChange={(e) => setMonth(e.target.value)} options={months} minWidth={190} />,
        ]}
      />
    </>
  );
}
